import React, { useEffect, useRef, useState } from 'react';

/* ─── 可调常量 ─────────────────────────────────────────── */
const CANVAS_SIZE = 512;
const AVATAR_SIZE = 400;
const AVATAR_SHIFT_Y = 40;
const MOCK_AVATAR = '/images/mock-avatar.jpg';
const OVERLAY_SRC = '/images/overlay.png';
/* ────────────────────────────────────────────────────── */

export default function AvatarBadge() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const [bgColor, setBgColor] = useState('#570484');
  const [avatarUrl, setAvatar] = useState(
    () => {
      const raw = localStorage.getItem('twitterAvatar') || MOCK_AVATAR;
      return toHiRes(raw);
    }
  );
  const [downloadUrl, setUrl] = useState('');
  const [showLink, setShowLink] = useState(false);

  function toHiRes(url: string) {
    return url.replace(/_normal(\.[a-z]+)$/i, '_400x400$1');
  }

  function hex2rgba(hex: string, alpha = 1) {
    const raw = hex.replace('#', '');
    const bigint = parseInt(
      raw.length === 3
        ? raw.split('').map((x) => x + x).join('')
        : raw,
      16
    );
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  /* 实时监听 localStorage 改变 */
  useEffect(() => {
    const fn = (e: StorageEvent) => {
      if (e.key === 'twitterAvatar') setAvatar(toHiRes(e.newValue || MOCK_AVATAR));
    };
    window.addEventListener('storage', fn);
    return () => window.removeEventListener('storage', fn);
  }, []);

  /* Follow-cursor glow */
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const move = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--x', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--y', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    el.addEventListener('mousemove', move);
    return () => el.removeEventListener('mousemove', move);
  }, []);

  /* 合成 PNG */
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;

    const DPR = window.devicePixelRatio || 1;
    c.width = CANVAS_SIZE * DPR;
    c.height = CANVAS_SIZE * DPR;
    c.style.width = `${CANVAS_SIZE}px`;
    c.style.height = `${CANVAS_SIZE}px`;

    const ctx = c.getContext('2d');
    if (!ctx) return;

    ctx.scale(DPR, DPR);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    Promise.all([loadImg(avatarUrl), loadImg(OVERLAY_SRC)]).then(([ava, overlay]) => {
      /* ---------- 基本参数 ---------- */
      const EXTRA_SHIFT_Y = -23;      // 上移
      const AVA_SCALE = 0.95;     // 95 %
      const FEATHER_PX = 6;        // 羽化宽度

      const avatarDrawSize = AVATAR_SIZE * AVA_SCALE;
      const off = (CANVAS_SIZE - AVATAR_SIZE) / 2;          // 保持旧逻辑
      const y = off + AVATAR_SHIFT_Y + EXTRA_SHIFT_Y;

      const cx = CANVAS_SIZE / 2;
      const cy = CANVAS_SIZE / 2 + AVATAR_SHIFT_Y + EXTRA_SHIFT_Y;
      const r = avatarDrawSize / 2;

      ctx.drawImage(overlay, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

      /* ---------- 画圆形头像 ---------- */
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(ava, off, y, AVATAR_SIZE, AVATAR_SIZE);
      ctx.restore();

      /* ---------- 羽化透明蒙版 ---------- */
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';          // 把渐变刻进 Alpha

      const innerRGBA = 'rgba(0,0,0,0)';
      const outerRGBA = hex2rgba(bgColor, 0.7);

      const grad = ctx.createRadialGradient(
        cx, cy, r - FEATHER_PX,   // 起点：内圈
        cx, cy, r                 // 终点：外圈
      );
      grad.addColorStop(0, innerRGBA);
      grad.addColorStop(1, outerRGBA);

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();


      /* ---------- 导出 ---------- */
      c.toBlob((b) => b && setUrl(URL.createObjectURL(b)));
    });
  }, [bgColor, avatarUrl]);

  /* 下载 & 显示跳转提示 */
  const handleDownload = () => {
    if (!downloadUrl) return;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'twitter_badge.png';
    a.click();
    setShowLink(true);
  };

  const twitterURL =
    /iPhone|Android|Mobile/i.test(navigator.userAgent)
      ? 'https://mobile.twitter.com/settings/profile'
      : 'https://twitter.com/settings/profile';

  return (
    <div style={wrapper}>
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        style={canvasStyle}
      />

      {/* 霓虹色环取色器 */}
      <div className="neo-picker" title="点击选择背景色">
        <input
          type="color"
          value={bgColor}
          onChange={(e) => setBgColor(e.target.value)}
        />
        <span className="neo-picker-icon">🖌</span>
      </div>

      {/* 下载按钮 */}
      <button
        ref={btnRef}
        className="neon-btn"
        onClick={handleDownload}
        disabled={!downloadUrl}
      >
        <span className="dl-arrow" />
        下载合成图片
      </button>

      {/* 提示链接 */}
      {showLink && (
        <div className="save-tip">
          <span>头像已保存 ✔</span>
          <a href={twitterURL} target="_blank" rel="noopener noreferrer">
            一键跳转&nbsp;Twitter&nbsp;更换&nbsp;→
          </a>
        </div>
      )}
    </div>
  );
}

/* ─── utils ─────────────────── */
function loadImg(src: string) {
  return new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.src = src;
    i.onload = () => res(i);
    i.onerror = rej;
  });
}

/* ─── inline 结构样式 ─────────── */
const wrapper: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  fontFamily: 'Inter, system-ui, sans-serif',
  color: '#eaeaea',
};

const canvasStyle: React.CSSProperties = {
  width: 320,
  height: 320,
  borderRadius: 24,
  boxShadow: '0 0 24px rgba(119,0,255,0.35)',
};

/* ─── 导出给 RewardsPage 用的 modalStyle ─── */
export const modalStyle = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backdropFilter: 'blur(6px)',
    background: 'rgba(15,0,30,0.65)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  } as React.CSSProperties,
  box: {
    position: 'relative',
    background:
      'linear-gradient(145deg, rgba(50,0,100,0.6) 0%, rgba(15,0,30,0.9) 100%)',
    border: '1px solid rgba(180,0,255,0.4)',
    boxShadow: '0 0 30px rgba(150,0,255,0.45)',
    borderRadius: 18,
    padding: '32px 24px',
    width: 'min(460px, 90vw)',
    maxHeight: '90vh',
    overflow: 'auto',
  } as React.CSSProperties,
  close: {
    position: 'absolute',
    top: 10,
    right: 14,
    border: 'none',
    background: 'transparent',
    fontSize: 26,
    color: '#fff',
    cursor: 'pointer',
  } as React.CSSProperties,
};
